import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Target, Trash2 } from "lucide-react";
import { Typography } from "@/components/ui/atoms/Typography";
import { dataTableSortHeader } from "@/components/ui/molecules/DataTable";
import type { Plan } from "@/models/plan";
import type { Invitation } from "@/hooks/useInvitations";
import { findPlanByCode } from "@/utils/plan.utils";
import { Button } from "@/components/ui/atoms/Button";
import { IconWrapper } from "@/components/ui/atoms/IconWrapper";
import { Tag } from "@/components/ui/atoms/Tag";
import { StatusPill } from "@/components/ui/atoms/StatusPill";

export type InvitationTableRow = Invitation & {
  tenantDisplay: string;
  planSearch: string;
};

export function mapInvitationToTableRow(
  invitation: Invitation,
  plans: Plan[]
): InvitationTableRow {
  const assignedPlan = invitation.assigned_plan_code
    ? findPlanByCode(plans, invitation.assigned_plan_code)
    : undefined;
  const planSearch =
    assignedPlan?.name ||
    invitation.assigned_plan_code ||
    "";

  return {
    ...invitation,
    tenantDisplay: invitation.tenant_name || "",
    planSearch,
  };
}

export interface InvitationsTableColumnsOptions {
  plans: Plan[];
  onRevoke: (invitation: Invitation) => void;
}

export function createInvitationsTableColumns({
  plans,
  onRevoke,
}: InvitationsTableColumnsOptions): ColumnDef<InvitationTableRow>[] {
  return [
    {
      accessorKey: "tenantDisplay",
      meta: { label: "Tenant" },
      header: dataTableSortHeader("Tenant"),
      size: 140,
      minSize: 100,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {row.original.tenant_name || "No tenant"}
        </Typography>
      ),
    },
    {
      accessorKey: "email",
      meta: { label: "Email" },
      header: dataTableSortHeader("Email"),
      size: 200,
      minSize: 140,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {row.original.email}
        </Typography>
      ),
    },
    {
      accessorKey: "planSearch",
      meta: { label: "Assigned plan" },
      header: dataTableSortHeader("Assigned plan"),
      size: 180,
      minSize: 120,
      cell: ({ row }) => {
        const invitation = row.original;
        const assignedPlan = invitation.assigned_plan_code
          ? findPlanByCode(plans, invitation.assigned_plan_code)
          : null;
        if (!invitation.assigned_plan_code) {
          return (
            <Typography variant="body-xs" weight="medium" color="muted" className="italic">
              No plan assigned
            </Typography>
          );
        }
        return (
          <Tag intent="primary" variant="subtle" className="inline-flex items-center gap-1.5">
            <IconWrapper size="sm"><Target /></IconWrapper>
            {assignedPlan?.name || invitation.assigned_plan_code}
          </Tag>
        );
      },
    },
    {
      accessorKey: "status",
      meta: { label: "Status" },
      header: dataTableSortHeader("Status"),
      size: 120,
      minSize: 96,
      cell: ({ row }) => {
        const invitation = row.original;
        const isAccepted = invitation.status === "accepted";
        const isExpired = invitation.status === "expired";
        return (
          <StatusPill
            variant={isAccepted ? "success" : isExpired ? "error" : "info"}
          >
            {isAccepted ? "Accepted" : isExpired ? "Expired" : "Pending"}
          </StatusPill>
        );
      },
    },
    {
      accessorKey: "expires_at",
      meta: { label: "Expires" },
      header: dataTableSortHeader("Expires"),
      size: 130,
      minSize: 100,
      cell: ({ row }) => (
        <Typography variant="body-xs" weight="medium" color="secondary">
          {format(new Date(row.original.expires_at), "MMM dd, yyyy")}
        </Typography>
      ),
    },
    {
      accessorKey: "created_at",
      meta: { label: "Created" },
      header: dataTableSortHeader("Created"),
      size: 130,
      minSize: 100,
      cell: ({ row }) => (
        <Typography variant="body-xs" weight="medium" color="secondary">
          {format(new Date(row.original.created_at), "MMM dd, yyyy")}
        </Typography>
      ),
    },
    {
      id: "actions",
      meta: { label: "Actions" },
      header: () => (
        <span className="block text-center font-heading text-xs font-bold uppercase tracking-wider text-dark-invirtus">
          Actions
        </span>
      ),
      size: 110,
      minSize: 96,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Button type="contained"
            intent="danger-soft"
            size="xs"
            label="Revoke"
            title="Revoke invitation"
            aria-label="Revoke invitation"
            id={`revoke-invitation-${row.original.id}`}
            onClick={() => onRevoke(row.original)}
            icon={<Trash2 />}
          />
        </div>
      ),
    },
  ];
}
